import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Input, Spin } from 'antd';
import { CountryInfo, MobileNumberValue, PhoneInputProps } from "./type/phone-input.type";
import { countryKey, fetchCountries } from "./action/phone-input.action";
import "../../../styles/component/common/phone-input.scss";
import server from "../../../server";

const {Option} = Select;


const PhoneInput: React.FC<PhoneInputProps> = (
    {
        value,
        onChange,
        placeholder = 'Enter mobile number',
        disabled = false,
        className = '',
        size = 'middle',
    }
) => {
    const [countries, setCountries] = useState<CountryInfo[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<CountryInfo | null>(null);
    const [number, setNumber] = useState<string>('');
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);


    // ── 1. Load countries ──
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        fetchCountries()
            .then((data) => {
                if (cancelled) return;
                setCountries(data);
                setLoading(false);
            })
            .catch((err: Error) => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    async function onInit() {
        let cancelled = false;
        setLoading(true);
        const data: CountryInfo[] = await server.utils.countriesnow.getAllCountriesDialCodeAndFlag();

        if (data) {
            if (cancelled) return;
            setCountries(data);
            setLoading(false);
        } else {
            if (cancelled) return;
            setLoading(false);
        }
    }

    // ── 2. Resolve initial country once list is ready ──
    useEffect(() => {
        if (value) {
            setNumber(value.number || '');
            const match = countries.find(
                (c) => c.dialCode === value.countryCode
            );
            if (match) setSelectedCountry(match);
        }
    }, [value, countries]);

    // ── Emit ─────────────────────────────────────────────────────────────────
    const emit = useCallback((country: CountryInfo | null, num: string) => {
        onChange?.({
            countryCode: country?.dialCode ?? '',
            number: num,
        });
    }, [onChange]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleCountryChange = useCallback((key: string) => {
            console.log('countryKey:', key)
            const country = countries.find((c: CountryInfo) => countryKey(c) === key) ?? null;
            setSelectedCountry(country);
            setSearchText('');
            emit(country, number);
        },
        [countries, emit]
    );

    const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value.replace(/[^\d\s\-().]/g, '');
            setNumber(raw);
            emit(selectedCountry, raw);
        },
        [emit]
    );

    // ── Filtered dropdown list ────────────────────────────────────────────────
    const filtered = searchText
        ? countries.filter((c) =>
            c.name.toLowerCase().includes(searchText.toLowerCase()) ||
            c.dialCode.includes(searchText)
        ) : countries;

    // ── Country selector trigger label: flag + dial code ─────────────────────
    const renderTriggerLabel = (country: CountryInfo) => (
        <span className="pn-country-select__label">
            <img
                src={country.flag}
                alt="country flag"
                width={size === "small" ? 20 : 25}
                height={size === "small" ? 15 : 18}
            />
            <span className="pn-country-select__code">{country.dialCode}</span>
        </span>
    );

    // ── Select addon ─────────────────────────────────────────────────────────
    const selectBefore = (
        <Select
            className={`pn-country-select pn-country-select-${size}`}
            popupClassName={`pn-country-dropdown ${size}`}
            value={selectedCountry ? countryKey(selectedCountry) : undefined}
            onChange={handleCountryChange}
            showSearch
            searchValue={searchText}
            onSearch={setSearchText}
            filterOption={false}
            loading={loading}
            disabled={disabled || loading}
            popupMatchSelectWidth={320}
            optionLabelProp="label"
            placeholder={loading ? '…' : 'Select'}
            size={size}
            notFoundContent={
                loading ? (
                    <div className="pn-country-dropdown__loading">
                        <Spin size="small"/>
                    </div>
                ) : (
                    <span className="pn-country-dropdown__empty">No results</span>
                )
            }
        >
            {filtered.map((country: CountryInfo) => (
                <Option
                    key={countryKey(country)}
                    value={countryKey(country)}
                    label={renderTriggerLabel(country)}
                >
                    <span className="pn-country-dropdown__option">
                        <img
                            src={country.flag}
                            alt="country flag"
                            width="25"
                            height="18"
                        />
                        {/*<span className="pn-country-dropdown__flag">{country.unicodeFlag}</span>*/}
                        <p className="pn-country-dropdown__name">
                            {country.name}
                            <span className="pn-country-dropdown__unicode">{country.unicodeFlag}</span>
                        </p>
                        <span className="pn-country-dropdown__code">{country.dialCode}</span>
                    </span>
                </Option>
            ))}
        </Select>
    );

    return (
        <div
            className={`phone-input phone-input-${size} ${className}`.trim()}
            data-size={size}
        >
            <Input
                name="phone-input"
                className="phone-input__field"
                addonBefore={selectBefore}
                value={number}
                onChange={handleNumberChange}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={20}
                type="tel"
                size={size}
            />
        </div>
    );
};

export default PhoneInput;
